import { useCallback, useState, type FormEvent, type ReactNode } from 'react';
import {
  changePassword, deleteAccount, dismissRecoveryCode, logIn, logOut, newRecoveryCode, recoverAccount, resolveConflict, signUp, syncNow, useAccount,
  type AccountSnapshot,
} from '@/cloud/account';
import { apiMessage } from '@/cloud/api';
import { hasApiKey } from '@/ai/anthropic';
import { useAppState } from '@/app/hooks';
import { Link } from '@/app/router';
import type { AppState } from '@/core/state';
import { relTime } from '@/core/util/misc';
import { levelInfo } from '@/engine/progression';
import { Modal, PageHead, Sys, Tabs } from '@/ui/hud';
import { Icon } from '@/ui/Icon';
import { toast } from '@/ui/toast';
import { L, num } from '@/i18n';

/* ------------------------------------------------------------------ */
/* Shared bits                                                          */
/* ------------------------------------------------------------------ */

function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <label className="field">
      <span className="label">{label}</span>
      {children}
      {hint && <span className="tiny faint">{hint}</span>}
    </label>
  );
}

/** Runs an async action with busy + error state. */
function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      return true;
    } catch (e) {
      setError(apiMessage(e));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, setError, run };
}

const ErrorLine = ({ text }: { text: string }) => (text ? <p className="callout amber small" role="alert"><Icon name="info" />{text}</p> : null);

export function syncLabel(a: AccountSnapshot): string {
  switch (a.sync) {
    case 'saving': return L('Saving…', 'Lagrer …');
    case 'saved': return a.lastSyncedAt ? L(`Saved ${relTime(a.lastSyncedAt)}`, `Lagret ${relTime(a.lastSyncedAt)}`) : L('Saved', 'Lagret');
    case 'offline': return L('Offline — will save when you are back online', 'Frakoblet — lagres når du er på nett igjen');
    case 'error': return L('Could not save — retrying', 'Kunne ikke lagre — prøver igjen');
    default: return L('Up to date', 'Oppdatert');
  }
}

/* ------------------------------------------------------------------ */
/* Log in / sign up / recover                                           */
/* ------------------------------------------------------------------ */

export type AuthTab = 'login' | 'signup' | 'recover';

export function AuthForms({ initial = 'login', onDone }: { initial?: AuthTab; onDone?: (done: AuthTab) => void }) {
  const [tab, setTab] = useState<AuthTab>(initial);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [code, setCode] = useState('');
  const { busy, error, setError, run } = useAction();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (tab !== 'login' && password !== repeat) {
      setError(L('The two passwords are not the same.', 'De to passordene er ikke like.'));
      return;
    }
    const ok = await run(() => (tab === 'login' ? logIn(username, password) : tab === 'signup' ? signUp(username, password) : recoverAccount(username, code, password)));
    if (ok) {
      toast(tab === 'signup' ? L('Account created', 'Kontoen er opprettet') : L('Logged in', 'Logget inn'), L('Your progress now saves to your account.', 'Fremgangen din lagres nå på kontoen din.'));
      onDone?.(tab);
    }
  };

  return (
    <div className="stack auth-forms">
      <Tabs<AuthTab> value={tab} onChange={(t) => { setTab(t); setError(''); }} items={[
        { id: 'login', label: L('Log in', 'Logg inn') },
        { id: 'signup', label: L('Create account', 'Opprett konto') },
        { id: 'recover', label: L('Forgot password', 'Glemt passord') },
      ]} />
      <form className="stack" onSubmit={submit}>
        <Field label={L('Username', 'Brukernavn')} hint={tab === 'signup' ? L('3–24 letters, numbers, . _ or -. Don\'t use your full name.', '3–24 bokstaver, tall, . _ eller -. Ikke bruk fullt navn.') : undefined}>
          <input className="input" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" autoCapitalize="none" spellCheck={false} maxLength={24} required />
        </Field>
        {tab === 'recover' && (
          <Field label={L('Recovery code', 'Gjenopprettingskode')} hint={L('The code you saved when you created the account, e.g. 7KQM-X3PA-RZ9D-HW4C.', 'Koden du lagret da du opprettet kontoen, f.eks. 7KQM-X3PA-RZ9D-HW4C.')}>
            <input className="input" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={40} required />
          </Field>
        )}
        <Field label={tab === 'recover' ? L('New password', 'Nytt passord') : L('Password', 'Passord')} hint={tab !== 'login' ? L('At least 8 characters. A few words together is easy to remember and hard to guess.', 'Minst 8 tegn. Noen ord satt sammen er lette å huske og vanskelige å gjette.') : undefined}>
          <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={tab === 'login' ? 'current-password' : 'new-password'} maxLength={200} required />
        </Field>
        {tab !== 'login' && (
          <Field label={L('Repeat password', 'Gjenta passordet')}>
            <input className="input" type="password" value={repeat} onChange={(e) => setRepeat(e.target.value)} autoComplete="new-password" maxLength={200} required />
          </Field>
        )}
        <ErrorLine text={error} />
        <button className="btn primary" type="submit" disabled={busy}>
          <Icon name={tab === 'recover' ? 'unlock' : 'arrow-right'} />
          {busy ? L('Please wait…', 'Vent litt …') : tab === 'login' ? L('Log in', 'Logg inn') : tab === 'signup' ? L('Create account', 'Opprett konto') : L('Set new password', 'Sett nytt passord')}
        </button>
      </form>
      {tab === 'signup' && (
        <p className="tiny faint">{L(
          'No email needed. Studio stores only your username, a scrambled (hashed) password and your learning progress. You will get a recovery code — keep it safe, it is the only way back in if you forget your password. Under 13? Ask a parent first.',
          'Ingen e-post trengs. Studio lagrer bare brukernavnet, et kryptert (hashet) passord og læringsfremgangen din. Du får en gjenopprettingskode — ta vare på den, den er den eneste veien inn hvis du glemmer passordet. Under 13 år? Spør en forelder først.',
        )}</p>
      )}
      {tab === 'recover' && (
        <p className="tiny faint">{L('Lost the recovery code too? Then the account cannot be recovered — Studio keeps no email or other way to prove who you are.', 'Mistet du gjenopprettingskoden også? Da kan kontoen ikke gjenopprettes — Studio har ingen e-post eller annen måte å bekrefte hvem du er på.')}</p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Dialogs (mounted once at app level)                                  */
/* ------------------------------------------------------------------ */

function RecoveryCodeDialog({ code }: { code: string }) {
  const [saved, setSaved] = useState(false);
  const close = useCallback(() => {
    if (saved) dismissRecoveryCode();
  }, [saved]);
  const download = () => {
    const blob = new Blob([`Studio\n${L('Recovery code', 'Gjenopprettingskode')}: ${code}\n`], { type: 'text/plain' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'studio-recovery-code.txt';
    a.click();
    URL.revokeObjectURL(a.href);
    setSaved(true);
  };
  return (
    <Modal onClose={close} label={L('Your recovery code', 'Gjenopprettingskoden din')}>
      <div className="stack">
        <Sys code="KEY">{L('Save your recovery code', 'Lagre gjenopprettingskoden')}</Sys>
        <p className="small">{L('If you forget your password, this code is the only way back into your account. It is shown only once.', 'Hvis du glemmer passordet, er denne koden den eneste veien tilbake til kontoen. Den vises bare én gang.')}</p>
        <div className="recovery-code">{code}</div>
        <div className="row wrap">
          <button className="btn sm" onClick={() => { void navigator.clipboard?.writeText(code).then(() => setSaved(true)); }}><Icon name="check" /> {L('Copy', 'Kopier')}</button>
          <button className="btn sm ghost" onClick={download}><Icon name="arrow-right" /> {L('Download as file', 'Last ned som fil')}</button>
        </div>
        <p className="tiny faint">{L('Write it down or store it somewhere safe — not in a message to friends.', 'Skriv den ned eller lagre den et trygt sted — ikke i en melding til venner.')}</p>
        <label className="row small"><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} /> {L('I have saved the code', 'Jeg har lagret koden')}</label>
        <button className="btn primary" disabled={!saved} onClick={dismissRecoveryCode}>{L('Done', 'Ferdig')}</button>
      </div>
    </Modal>
  );
}

function summary(s: AppState) {
  return {
    name: s.profile?.name ?? '—',
    level: levelInfo(s.xp.total).level,
    xp: s.xp.total,
    answers: s.attempts.length,
  };
}

function exportBackup(state: AppState) {
  const blob = new Blob([JSON.stringify(state, null, 1)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `studio-backup-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  URL.revokeObjectURL(a.href);
}

function ConflictDialog({ account }: { account: AccountSnapshot }) {
  const local = useAppState();
  const c = account.conflict!;
  const [busy, setBusy] = useState(false);
  const noop = useCallback(() => undefined, []);
  const choose = async (keep: 'account' | 'device') => {
    setBusy(true);
    try {
      await resolveConflict(keep);
    } finally {
      setBusy(false);
    }
  };
  const Card = ({ title, s, when }: { title: string; s: ReturnType<typeof summary>; when: string }) => (
    <div className="plate tight stack" style={{ flex: 1, minWidth: 200 }}>
      <Sys>{title}</Sys>
      <div style={{ fontWeight: 600 }}>{s.name}</div>
      <div className="small muted">{L(`Level ${s.level} · ${num(s.xp)} XP · ${num(s.answers)} answers`, `Nivå ${s.level} · ${num(s.xp)} XP · ${num(s.answers)} svar`)}</div>
      <div className="tiny faint">{when}</div>
    </div>
  );
  return (
    <Modal onClose={noop} label={L('Choose which progress to keep', 'Velg hvilken fremgang du vil beholde')}>
      <div className="stack">
        <Sys code="SYNC">{L('Two versions of your progress', 'To versjoner av fremgangen din')}</Sys>
        <p className="small">{c.reason === 'login'
          ? L(`This device already has progress that is not in the account "${account.user?.displayName}". Which one should Studio keep?`, `Denne enheten har allerede fremgang som ikke ligger på kontoen «${account.user?.displayName}». Hvilken skal Studio beholde?`)
          : L('Your progress changed both on this device and on another device since they last synced. Which one should Studio keep?', 'Fremgangen din er endret både på denne enheten og på en annen enhet siden sist de var synkronisert. Hvilken skal Studio beholde?')}</p>
        <div className="row wrap" style={{ gap: 10, alignItems: 'stretch' }}>
          <Card title={L('In the account', 'På kontoen')} s={summary(c.remote)} when={c.remoteUpdatedAt ? L(`Saved ${relTime(c.remoteUpdatedAt)}`, `Lagret ${relTime(c.remoteUpdatedAt)}`) : ''} />
          <Card title={L('On this device', 'På denne enheten')} s={summary(local)} when={L('Not yet saved to the account', 'Ikke lagret på kontoen ennå')} />
        </div>
        <div className="row wrap">
          <button className="btn primary" disabled={busy} onClick={() => void choose('account')}>{L('Keep the account version', 'Behold kontoens versjon')}</button>
          <button className="btn" disabled={busy} onClick={() => void choose('device')}>{L('Keep this device\'s version', 'Behold denne enhetens versjon')}</button>
        </div>
        <p className="tiny faint">{L('The other version is replaced. Want to keep a copy of this device\'s version first?', 'Den andre versjonen blir erstattet. Vil du ta vare på en kopi av denne enhetens versjon først?')} <button className="btn ghost sm" onClick={() => exportBackup(local)}>{L('Download a backup', 'Last ned sikkerhetskopi')}</button></p>
      </div>
    </Modal>
  );
}

export function AccountDialogs() {
  const account = useAccount();
  if (account.recoveryCode) return <RecoveryCodeDialog code={account.recoveryCode} />;
  if (account.conflict) return <ConflictDialog account={account} />;
  return null;
}

/* ------------------------------------------------------------------ */
/* Account page                                                         */
/* ------------------------------------------------------------------ */

function LogOutPanel() {
  const [confirm, setConfirm] = useState<null | 'normal' | 'unsynced'>(null);
  const [everywhere, setEverywhere] = useState(false);
  const [removeKey, setRemoveKey] = useState(hasApiKey());
  const { busy, error, run } = useAction();
  const go = async (force: boolean) => {
    let result = { ok: false, unsynced: false } as { ok: boolean; unsynced?: boolean };
    const ok = await run(async () => { result = await logOut({ everywhere, force, removeAiKey: removeKey }); });
    if (ok && !result.ok && result.unsynced) setConfirm('unsynced');
  };
  if (!confirm) return <button className="btn sm" onClick={() => setConfirm('normal')}><Icon name="arrow-left" /> {L('Log out…', 'Logg ut …')}</button>;
  return (
    <div className="stack plate tight">
      {confirm === 'unsynced'
        ? <p className="callout amber small"><Icon name="info" />{L('Some progress could not be saved to your account (no connection?). If you log out now, it is lost.', 'Noe fremgang kunne ikke lagres på kontoen (ingen tilkobling?). Logger du ut nå, går den tapt.')}</p>
        : <p className="small">{L('Logging out removes your progress from this device (it stays safe in your account), so the next person here starts fresh.', 'Når du logger ut, fjernes fremgangen fra denne enheten (den er trygt lagret på kontoen), så neste person her starter på nytt.')}</p>}
      <label className="row small"><input type="checkbox" checked={everywhere} onChange={(e) => setEverywhere(e.target.checked)} /> {L('Log out on all my devices', 'Logg ut på alle enhetene mine')}</label>
      {hasApiKey() && <label className="row small"><input type="checkbox" checked={removeKey} onChange={(e) => setRemoveKey(e.target.checked)} /> {L('Also remove the AI key from this device', 'Fjern også KI-nøkkelen fra denne enheten')}</label>}
      <ErrorLine text={error} />
      <div className="row wrap">
        <button className={`btn sm ${confirm === 'unsynced' ? 'danger' : 'primary'}`} disabled={busy} onClick={() => void go(confirm === 'unsynced')}>
          {confirm === 'unsynced' ? L('Log out and lose unsaved progress', 'Logg ut og mist ulagret fremgang') : L('Log out', 'Logg ut')}
        </button>
        <button className="btn sm ghost" onClick={() => setConfirm(null)}>{L('Cancel', 'Avbryt')}</button>
      </div>
    </div>
  );
}

function PasswordPanel() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const { busy, error, setError, run } = useAction();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (next !== repeat) return setError(L('The two new passwords are not the same.', 'De to nye passordene er ikke like.'));
    if (await run(() => changePassword(current, next))) {
      setCurrent(''); setNext(''); setRepeat('');
      toast(L('Password changed', 'Passordet er endret'), L('Other devices have been logged out.', 'Andre enheter er logget ut.'));
    }
  };
  return (
    <form className="stack" onSubmit={submit}>
      <Field label={L('Current password', 'Nåværende passord')}><input className="input" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} required maxLength={200} /></Field>
      <Field label={L('New password', 'Nytt passord')}><input className="input" type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} required maxLength={200} /></Field>
      <Field label={L('Repeat new password', 'Gjenta nytt passord')}><input className="input" type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} required maxLength={200} /></Field>
      <ErrorLine text={error} />
      <div><button className="btn sm" disabled={busy}><Icon name="lock" /> {L('Change password', 'Endre passord')}</button></div>
    </form>
  );
}

function PasswordConfirm({ label, danger, onConfirm, children }: { label: string; danger?: boolean; onConfirm: (password: string) => Promise<unknown>; children?: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const { busy, error, run } = useAction();
  if (!open) return <button className={`btn sm ${danger ? 'danger' : 'ghost'}`} onClick={() => setOpen(true)}>{label}…</button>;
  return (
    <form className="stack plate tight" onSubmit={async (e) => { e.preventDefault(); if (await run(() => onConfirm(password))) { setOpen(false); setPassword(''); } }}>
      {children}
      <Field label={L('Confirm with your password', 'Bekreft med passordet ditt')}><input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required maxLength={200} /></Field>
      <ErrorLine text={error} />
      <div className="row wrap">
        <button className={`btn sm ${danger ? 'danger' : ''}`} disabled={busy}>{label}</button>
        <button type="button" className="btn sm ghost" onClick={() => setOpen(false)}>{L('Cancel', 'Avbryt')}</button>
      </div>
    </form>
  );
}

function SignedIn({ account }: { account: AccountSnapshot }) {
  const [keepAsGuest, setKeepAsGuest] = useState(false);
  const user = account.user!;
  return (
    <div className="grid two">
      <section className="plate stack">
        <Sys code="ID">{L('Your account', 'Kontoen din')}</Sys>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600 }}>{user.displayName}</div>
          <div className="small muted">@{user.username}{user.createdAt ? ` · ${L('member since', 'medlem siden')} ${new Date(user.createdAt).toLocaleDateString()}` : ''}</div>
        </div>
        <div className="small"><span className={`sync-dot ${account.sync}`} />{syncLabel(account)}</div>
        <p className="tiny faint">{L('Your progress saves to the account automatically a few seconds after each change, and loads on any device where you log in.', 'Fremgangen lagres automatisk på kontoen noen sekunder etter hver endring, og hentes på alle enheter der du logger inn.')}</p>
        <div className="row wrap">
          <button className="btn sm" onClick={() => void syncNow()} disabled={account.sync === 'saving'}><Icon name="refresh" /> {L('Sync now', 'Synkroniser nå')}</button>
        </div>
        <LogOutPanel />
      </section>

      <section className="plate stack">
        <Sys code="SEC">{L('Password & recovery', 'Passord og gjenoppretting')}</Sys>
        <PasswordPanel />
        <p className="tiny faint">{L('Lost your recovery code? Make a new one — the old code stops working.', 'Mistet gjenopprettingskoden? Lag en ny — den gamle slutter å virke.')}</p>
        <div><PasswordConfirm label={L('Make a new recovery code', 'Lag ny gjenopprettingskode')} onConfirm={newRecoveryCode} /></div>
      </section>

      <section className="plate stack">
        <Sys code="DATA">{L('Your data & privacy', 'Dataene dine og personvern')}</Sys>
        <ul className="small muted" style={{ paddingLeft: 18, margin: 0 }}>
          <li>{L('Stored on Studio\'s server: your username, a hash of your scrambled password (never the password itself), a hashed recovery code and your learning progress.', 'Lagret på Studios server: brukernavnet, en hash av det krypterte passordet (aldri selve passordet), en hashet gjenopprettingskode og læringsfremgangen din.')}</li>
          <li>{L('Never stored there: email, real name, your AI key or your chat conversations.', 'Lagres aldri der: e-post, ekte navn, KI-nøkkelen eller chat-samtalene dine.')}</li>
          <li>{L('Login attempts are kept for up to a day to stop password guessing (IP addresses only in scrambled form).', 'Innloggingsforsøk lagres i opptil ett døgn for å stoppe passordgjetting (IP-adresser bare i kryptert form).')}</li>
        </ul>
        <div className="row wrap"><Link to="/settings" className="btn ghost sm"><Icon name="arrow-right" /> {L('Export a backup (Settings)', 'Eksporter sikkerhetskopi (Innstillinger)')}</Link></div>
        <PasswordConfirm label={L('Delete my account', 'Slett kontoen min')} danger onConfirm={(p) => deleteAccount(p, keepAsGuest)}>
          <p className="callout amber small"><Icon name="info" />{L('This permanently deletes the account and everything saved in it. It cannot be undone.', 'Dette sletter kontoen og alt som er lagret på den for godt. Det kan ikke angres.')}</p>
          <label className="row small"><input type="checkbox" checked={keepAsGuest} onChange={(e) => setKeepAsGuest(e.target.checked)} /> {L('Keep a copy of my progress on this device (without an account)', 'Behold en kopi av fremgangen på denne enheten (uten konto)')}</label>
        </PasswordConfirm>
      </section>
    </div>
  );
}

export function Account() {
  const account = useAccount();
  return (
    <div className="page">
      <PageHead code="ACC" title={L('Account', 'Konto')} lede={L('Optional: an account saves your progress safely and lets you continue on any device. Studio works fully without one.', 'Valgfritt: en konto lagrer fremgangen trygt og lar deg fortsette på hvilken som helst enhet. Studio fungerer fullt ut uten.')} />
      {account.phase === 'checking' && <p className="muted small">{L('Checking…', 'Sjekker …')}</p>}
      {account.phase === 'unavailable' && <p className="callout amber"><Icon name="info" />{L('Accounts are not available on this copy of Studio right now. Everything still works and is saved in this browser.', 'Kontoer er ikke tilgjengelige i denne versjonen av Studio akkurat nå. Alt fungerer fortsatt og lagres i denne nettleseren.')}</p>}
      {account.phase === 'signed-in' && <SignedIn account={account} />}
      {account.phase === 'guest' && (
        <div className="grid two">
          <section className="plate stack">
            {account.sessionEnded && <p className="callout amber small"><Icon name="info" />{L('You were logged out (the session ended or you logged out on another device). Log in again to keep saving this progress to your account.', 'Du ble logget ut (økten gikk ut, eller du logget ut på en annen enhet). Logg inn igjen for å fortsette å lagre fremgangen på kontoen.')}</p>}
            <AuthForms initial={account.sessionEnded ? 'login' : 'signup'} />
          </section>
          <section className="plate stack">
            <Sys code="WHY">{L('Why make an account?', 'Hvorfor lage en konto?')}</Sys>
            <ul className="small muted" style={{ paddingLeft: 18, margin: 0 }}>
              <li>{L('Your progress is saved safely, even if this browser is cleared.', 'Fremgangen er trygt lagret, selv om nettleseren tømmes.')}</li>
              <li>{L('Continue on another computer, tablet or phone.', 'Fortsett på en annen datamaskin, et nettbrett eller en mobil.')}</li>
              <li>{L('On a shared school computer, log out and your progress leaves with you.', 'På en delt skole-PC logger du ut, og fremgangen følger med deg.')}</li>
              <li>{L('Progress you already have on this device moves into the new account.', 'Fremgang du allerede har på denne enheten flyttes inn i den nye kontoen.')}</li>
            </ul>
            <p className="tiny faint">{L('No email, no real name, no ads. Your password never leaves your device: the browser scrambles it (600 000 rounds of PBKDF2) and the server keeps only a salted hash of the result. Login is protected against password guessing.', 'Ingen e-post, ikke ekte navn, ingen reklame. Passordet forlater aldri enheten din: nettleseren krypterer det (600 000 runder PBKDF2), og serveren lagrer bare en saltet hash av resultatet. Innloggingen er beskyttet mot passordgjetting.')}</p>
          </section>
        </div>
      )}
    </div>
  );
}
